import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  costProfiles: [],
};

function handleRetrieveCostProfilesRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleRetrieveCostProfilesSuccess(
  state: State,
  costProfiles: Boundaries["retrieveCostProfiles"]["success"],
): State {
  return { costProfiles, request: setRequestStateToSuccess() };
}

function handleRetrieveCostProfilesFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleCreateCostProfile(
  state: State,
  { costProfile }: Boundaries["createCostProfile"]["success"],
): State {
  return { ...state, costProfiles: [...state.costProfiles, costProfile] };
}

function handleEditCostProfile(
  state: State,
  costProfile: Boundaries["editCostProfile"]["success"],
): State {
  const index = state.costProfiles.findIndex(
    ({ uuid }) => costProfile.uuid === uuid,
  );

  return {
    ...state,
    costProfiles:
      index !== -1
        ? [
            ...state.costProfiles.slice(0, index),
            { ...state.costProfiles[index], ...costProfile },
            ...state.costProfiles.slice(index + 1),
          ]
        : state.costProfiles,
  };
}

function handleRemoveCostProfile(
  state: State,
  costProfile: Boundaries["removeCostProfile"]["success"],
): State {
  return {
    ...state,
    costProfiles: state.costProfiles.filter((c) => c.uuid !== costProfile.uuid),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveCostProfilesRequested]: handleRetrieveCostProfilesRequest,
    [events.retrieveCostProfilesSucceeded]: handleRetrieveCostProfilesSuccess,
    [events.retrieveCostProfilesFailed]: handleRetrieveCostProfilesFailure,
    [events.createCostProfileSucceeded]: handleCreateCostProfile,
    [events.editCostProfileSucceeded]: handleEditCostProfile,
    [events.removeCostProfileSucceeded]: handleRemoveCostProfile,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
